import React from 'react';
import { cn } from './cn';

type Size = 'sm' | 'md' | 'lg';

export interface SpinnerProps {
  size?: Size;
  className?: string;
  ariaLabel?: string;
}

const sizes: Record<Size, number> = { sm: 32, md: 48, lg: 64 };

/**
 * Cosmic spinner — 8 stars on a circle, rotating with cascading per-star pulse.
 * Reduced-motion: animation halts (handled by global @media reduce in theme.css).
 */
export const Spinner: React.FC<SpinnerProps> = ({ size = 'md', className, ariaLabel = 'Loading' }) => {
  const dim = sizes[size];
  const radius = dim * 0.4;
  const center = dim / 2;
  const stars = Array.from({ length: 8 }, (_, i) => {
    const angle = (i / 8) * Math.PI * 2 - Math.PI / 2;
    return {
      x: center + Math.cos(angle) * radius,
      y: center + Math.sin(angle) * radius,
      delay: i * 200,
    };
  });

  return (
    <span
      role="status"
      aria-label={ariaLabel}
      aria-live="polite"
      className={cn('inline-flex animate-spin-slow text-accent-gold', className)}
      style={{ width: dim, height: dim }}
    >
      <svg width={dim} height={dim} viewBox={`0 0 ${dim} ${dim}`} aria-hidden>
        {stars.map((s, i) => (
          <circle
            key={i}
            cx={s.x}
            cy={s.y}
            r={dim * 0.06}
            fill="currentColor"
            style={{
              animation: `pulse-cosmic 1.6s ease-in-out infinite`,
              animationDelay: `${s.delay}ms`,
              transformOrigin: `${s.x}px ${s.y}px`,
            }}
          />
        ))}
      </svg>
      <style>{`
        @keyframes pulse-cosmic {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50%      { opacity: 1.0; transform: scale(1.0); }
        }
      `}</style>
    </span>
  );
};
